import React, { useEffect, useState } from 'react';
import useInView from './useInView';
import useMotionPrefs from './useMotionPrefs';

/* ==================================================================
   MatchScore
   ------------------------------------------------------------------
   HyrSkill's answer to "why is this in front of me?", so it gets to
   be the one number that moves: it counts 0 -> score once, with a
   compact meter filling alongside it. A badge, deliberately - not a
   ring - because it has to sit inside a dense card header without
   taking the title's place.
   ================================================================== */

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

function tone(pct) {
  if (pct >= 80) return 'strong';
  if (pct >= 60) return 'good';
  if (pct >= 40) return 'fair';
  return 'low';
}

export default function MatchScore({
  value = 0,
  size = 'md',
  label = 'match',
  duration = 700,
  className = '',
  title,
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const { reduced } = useMotionPrefs();
  const [ref, inView] = useInView({ threshold: 0.4, skip: reduced });
  const [shown, setShown] = useState(reduced ? pct : 0);

  useEffect(() => {
    if (reduced) { setShown(pct); return undefined; }
    if (!inView) return undefined;

    let frame = 0;
    let start = 0;
    const tick = (now) => {
      if (!start) start = now;
      const t = Math.min((now - start) / duration, 1);
      setShown(Math.round(pct * easeOutExpo(t)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, pct, duration, reduced]);

  return (
    <span
      ref={ref}
      className={`ui-match ui-match--${tone(pct)}${size === 'lg' ? ' ui-match--lg' : ''} ${className}`.trim()}
      style={{ '--ui-match-p': inView || reduced ? pct / 100 : 0 }}
      title={title || `${pct}% match with your profile`}
    >
      <span className="ui-match__meter" aria-hidden="true" />
      <span>{shown}% {label}</span>
    </span>
  );
}
